import React, { ReactNode, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";

type Props = {
	emotionCacheKey: string;
	children: ReactNode;
};

/**
 * A wrapper component that creates a shadow root and an emotion cache for the children.
 * This is used to prevent theme leaks from the children to the parent. Useful for microfrontends.
 */
export function ShadowDOMWrapper(props: Props): JSX.Element {
	const hostRef = useRef<HTMLDivElement>(null);
	const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);
	const shadowRootRef = useRef<ShadowRoot | null>(null);

	useEffect(() => {
		if (!hostRef.current) return;

		let shadowRoot: ShadowRoot;
		if (hostRef.current.shadowRoot) {
			shadowRoot = hostRef.current.shadowRoot;
		} else {
			shadowRoot = hostRef.current.attachShadow({ mode: "open" });
		}
		shadowRootRef.current = shadowRoot;

		const mountEl = document.createElement("div");
		shadowRoot.appendChild(mountEl);

		const emotionCache = createCache({
			key: props.emotionCacheKey,
			container: shadowRoot,
		});

		const root = createRoot(mountEl);
		rootRef.current = root;

		root.render(<CacheProvider value={emotionCache}>{props.children}</CacheProvider>);

		return () => {
			setTimeout(() => {
				root.unmount();
				if (shadowRootRef.current) {
					shadowRootRef.current.innerHTML = "";
				}
			}, 0);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (rootRef.current && shadowRootRef.current) {
			const emotionCache = createCache({
				key: props.emotionCacheKey,
				container: shadowRootRef.current,
			});

			rootRef.current.render(<CacheProvider value={emotionCache}>{props.children}</CacheProvider>);
		}
	}, [props.children, props.emotionCacheKey]);

	return <div ref={hostRef} data-testid="shadow-wrapper" />;
}
